import React, { useEffect, useState } from 'react';
import PageContainer from '../components/PageContainer';
import { getContentGaps, ContentGaps as GapsData, GapCluster } from '../api/gaps';
import { getDomains, Domain } from '../api/data';

const WINDOWS = [7, 30, 90] as const;

const ContentGapsPage: React.FC = () => {
  const [days, setDays] = useState<number>(30);
  const [domainId, setDomainId] = useState<string>('');
  const [domains, setDomains] = useState<Domain[]>([]);
  const [data, setData] = useState<GapsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDomains(1, 100).then(res => setDomains(res.items)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getContentGaps(days, domainId ? parseInt(domainId) : undefined)
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days, domainId]);

  return (
    <PageContainer
      title="Content Gaps"
      subtitle="Questions users asked that the chatbot couldn't answer — grouped, counted, and ready to become new knowledge units."
      actions={
        <div className="flex items-center gap-2">
          <select value={domainId} onChange={e => setDomainId(e.target.value)} className="input-base w-auto min-w-[150px] text-sm py-1.5">
            <option value="">All domains</option>
            {domains.map(d => <option key={d.id} value={d.id}>{d.name_en}</option>)}
          </select>
          <div className="inline-flex items-center bg-primary-100 rounded-lg p-0.5">
            {WINDOWS.map(w => (
              <button
                key={w}
                onClick={() => setDays(w)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  days === w ? 'bg-white text-primary-900 shadow-card' : 'text-primary-500 hover:text-primary-800'
                }`}
              >
                {w}d
              </button>
            ))}
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Analyzing questions…
          </div>
        </div>
      ) : error ? (
        <div className="card p-8">
          <div className="text-base font-medium text-primary-900">Couldn't load content gaps</div>
          <div className="mt-1 text-sm text-primary-500">{error}</div>
        </div>
      ) : data && (
        <>
          {/* Summary strip */}
          <div className="card grid grid-cols-3 overflow-hidden">
            <Stat label="Questions asked" value={String(data.total_messages)} />
            <Stat label="Went unanswered" value={String(data.unanswered_count)} dim={data.unanswered_count === 0} />
            <Stat label="Unanswered rate" value={`${Math.round(data.unanswered_rate * 100)}%`} dim={data.unanswered_count === 0} />
          </div>

          {data.clusters.length === 0 ? (
            <div className="card p-12 text-center">
              <p className="text-sm font-medium text-primary-800">No content gaps in this window 🎉</p>
              <p className="mt-1 text-sm text-primary-500">Every question got a grounded answer.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.clusters.map((c, i) => <ClusterRow key={i} cluster={c} rank={i + 1} />)}
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
};

const Stat: React.FC<{ label: string; value: string; dim?: boolean }> = ({ label, value, dim }) => (
  <div className="px-6 py-5 border-primary-200 [&:not(:last-child)]:border-e">
    <div className={`text-3xl font-semibold tabular-nums leading-tight ${dim ? 'text-primary-400' : 'text-primary-900'}`}>{value}</div>
    <div className="mt-1.5 eyebrow">{label}</div>
  </div>
);

const ClusterRow: React.FC<{ cluster: GapCluster; rank: number }> = ({ cluster, rank }) => {
  const [open, setOpen] = useState(false);
  const hasMore = cluster.examples.length > 1;
  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => hasMore && setOpen(!open)}
        className={`w-full flex items-start gap-4 px-5 py-4 text-left ${hasMore ? 'hover:bg-primary-50/60 cursor-pointer' : 'cursor-default'} transition-colors`}
      >
        <span className="flex-shrink-0 min-w-[38px] h-[26px] px-2 rounded-full bg-primary-50 text-primary-700 text-sm font-bold inline-flex items-center justify-center mt-0.5">
          ×{cluster.count}
        </span>
        <span className="flex-1 min-w-0">
          <span dir="auto" className="block text-[14.5px] text-primary-900 leading-relaxed">{cluster.question}</span>
          <span className="mt-1 flex items-center gap-2 flex-wrap text-xs text-primary-400">
            {cluster.domains.length > 0
              ? cluster.domains.map(d => (
                  <span key={d} className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-500">{d}</span>
                ))
              : <span className="px-1.5 py-0.5 rounded bg-primary-100 text-primary-500">General</span>}
            {cluster.last_asked && <span>last asked {new Date(cluster.last_asked).toLocaleDateString()}</span>}
          </span>
        </span>
        {hasMore && (
          <svg className={`w-4 h-4 mt-1.5 flex-shrink-0 text-primary-400 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        )}
      </button>
      {open && (
        <div className="border-t border-primary-100 px-5 py-3 bg-primary-50/40">
          <div className="eyebrow mb-2">All phrasings</div>
          <ul className="space-y-1.5">
            {cluster.examples.map((ex, i) => (
              <li key={i} dir="auto" className="text-sm text-primary-600 leading-relaxed flex items-baseline gap-2">
                <span className="text-primary-300 select-none">•</span>
                <span className="flex-1">{ex.question}</span>
                {ex.at && <span className="text-xs text-primary-400 whitespace-nowrap">{new Date(ex.at).toLocaleDateString()}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ContentGapsPage;
